import * as React from 'react';
import _ from 'lodash';
import { observer } from 'mobx-react';
import { DisplayType } from '../Models/Enums';
import {
	Dropdown,
	DropdownItemProps,
	DropdownProps,
	HtmlSpanProps,
	Label,
	SemanticShorthandItem,
	StrictDropdownProps,
	StrictDropdownItemProps,
} from 'semantic-ui-react';
import InputWrapper from 'Views/Components/Inputs/InputWrapper';
import classNames from 'classnames';
import { action, computed, observable, makeObservable } from 'mobx';
import CustomSpinner from '../Spinner/CustomSpinner';
import { BoundProp, getBoundValue, setBoundValue } from '../Inputs/BoundModelProperty';

export interface ComboboxOption<I> {
	/** The value to display in the combobox item dropdown */
	display: React.ReactNode;
	/**
	 * Optional rich display for the dropdown item only. When set, this is rendered
	 * inside the open dropdown list while `display` is still used for the closed
	 * trigger's selected-value label. Use for things like highlighting matched
	 * search-term substrings.
	 */
	content?: React.ReactNode;
	/** The value to select */
	value: I | undefined;
	/** In a multi combobox can the value be removed */
	isFixed?: boolean;
	/** Is the item currently disabled */
	disabled?: boolean;
	/** Additional classes. */
	className?: string;
	/** Additional text with less emphasis. */
	description?: SemanticShorthandItem<HtmlSpanProps>;
	/** Additional options for the dropdown item */
	optionProps?: StrictDropdownItemProps;
}

interface InternalComboboxProps<T, I> {
	/** The model to change the attribute of */
	model: T;
	/**
	 * The property on the model to change.
	 *
	 * `I | I[]` because a `multiple` combobox stores the whole selection on the property.
	 */
	modelProperty: BoundProp<T, I | I[]>;
	/**
	 * Gets an identifying property from the value object. This must be a unique value and not an object.
	 * By default this will get the value object itself
	 */
	getOptionValue?: (option: I | undefined) => undefined | boolean | number | string;
	/**
	 * A function to compare the model property to the selected option
	 * @param modelProperty The model property to compare
	 * @param option The option from the combobox
	 */
	optionEqualFunc?: (modelProperty: string | number | boolean | undefined, option: I | undefined) => boolean;
	/** The to display around the combobox */
	label: string | React.ReactNode;
	/** Weather the label is visible */
	labelVisible?: boolean;
	/** The tooltip to display */
	tooltip?: string | JSX.Element[];
	/** The display type to use */
	displayType?: DisplayType;
	/** The classname for to combobox */
	className?: string;
	/** Raw props that are passed through to the react-select component */
	inputProps?: DropdownProps;
	/** The placeholder text when the combobox is empty */
	placeholder?: string;
	/** A list of errors that are to be displayed around the combobox */
	errors?: string | string[];
	/** The minimum length of search string with can be searched, default to 1 */
	minSearchLength?: number;
	/** If the combobox is isDisabled */
	isDisabled?: boolean;
	/** If the field is required */
	isRequired?: boolean;
	/** Override of the onChange function. Using this will remove the model binding logic of the component */
	onChange?: (event: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => void;
	/** Action to perform after the onChange method is called */
	onAfterChange?: (event: React.SyntheticEvent<HTMLElement>, data: DropdownProps, oldValue?: I) => void;
	/** Is the select value clearable */
	isClearable?: boolean;
	/** An optional button set next to the label */
	sideButton?: React.ReactNode;

	tooltipIconClassName?: string;
}

export interface SyncComboboxProps<T, I> extends InternalComboboxProps<T, I> {
	/**
	 * The options on the dropdown
	 * An array of JSON objects that by default will have the form of {display: string, value: string}
	 *
	 * The key and value properties can be overriden with the getOptionLabel and getOptionValue props
	 */
	options: ComboboxOption<I | undefined>[];
	/** If the combobox is searchable */
	searchable?: boolean | ((options: DropdownItemProps[], value: string) => DropdownItemProps[]);
	/** If the combobox is in a loading state */
	loading?: boolean;
}

export interface AsyncComboboxProps<T, I> extends InternalComboboxProps<T, I> {
	/**
	 * The options on the dropdown
	 * A function that returns an promise resolving to an array of JSON objects that by default will have the form of
	 * {display: string, value: string}
	 *
	 * The key and value properties can be overriden with the getOptionLabel and getOptionValue props
	 */
	options: (input: string) => Promise<ComboboxOption<I | undefined>[]>;
	/**
	 * The initial options that are displayed in the combobox before any search occurs
	 */
	initialOptions?: () => Promise<ComboboxOption<I | undefined>[]>;
	/** Message shown when no results are found and no search has been performed yet */
	noResultsMessage?: string;
}

interface InternalSyncComboboxProps<T, I> extends SyncComboboxProps<T, I> {
	getOptionValue: (option: I | undefined) => undefined | boolean | number | string;
}

interface InternalAsyncComboboxProps<T, I> extends AsyncComboboxProps<T, I> {
	getOptionValue: (option: I | undefined) => undefined | boolean | number | string;
}

export type IComboboxProps<T, I> = AsyncComboboxProps<T, I> | SyncComboboxProps<T, I>;

/**
 * A dropdown selection menu for selecting an item from a list of options
 */
class ComboboxClass<T, I> extends React.Component<IComboboxProps<T, I>> {
	static defaultProps = {
		labelVisible: true,
		minSearchLength: 0,
		searchable: true,
	};

	private _reFetch?: () => void = undefined;
	public get reFetch() {
		if (this._reFetch) {
			return this._reFetch;
		}
		return _.noop;
	}

	private getOptionValue = (option: I | undefined) =>
		this.props.getOptionValue
			? this.props.getOptionValue(option)
			: (option as string | number | boolean | undefined);

	public render() {
		const {
			label,
			tooltip,
			tooltipIconClassName,
			modelProperty,
			isRequired,
			labelVisible,
			options,
			className,
			errors,
			displayType,
			sideButton,
		} = this.props;

		return (
			<InputWrapper
				className={classNames('input-group__dropdown', className)}
				label={label}
				errors={errors}
				labelVisible={labelVisible}
				tooltip={tooltip}
				tooltipIconClassName={tooltipIconClassName}
				displayType={displayType}
				inputName={modelProperty}
				isRequired={isRequired}
				sideButton={sideButton}
			>
				{typeof options === 'function' ? (
					<ObservedAsyncCombobox
						{...this.props}
						ref={ref => {
							this._reFetch = ref?.reFetch;
						}}
						options={options}
						getOptionValue={this.getOptionValue}
					/>
				) : (
					<ObservedSyncCombobox {...this.props} options={options} getOptionValue={this.getOptionValue} />
				)}
			</InputWrapper>
		);
	}
}

export const Combobox = observer(ComboboxClass);

class SyncComboboxClass<T, I> extends React.Component<InternalSyncComboboxProps<T, I>> {
	public render() {
		const {
			inputProps,
			loading,
			searchable,
			modelProperty,
			onAfterChange,
			optionEqualFunc,
			model,
			isClearable,
			getOptionValue,
			placeholder,
			onChange,
			options,
			isDisabled,
		} = this.props;

		return (
			<ObservedInnerCombobox
				model={model}
				modelProperty={modelProperty}
				getOptionValue={getOptionValue}
				optionResults={options}
				search={searchable}
				loading={loading}
				placeholder={placeholder}
				clearable={isClearable}
				onAfterChange={onAfterChange}
				disabled={isDisabled}
				onChange={onChange}
				optionEqualFunc={optionEqualFunc}
				{...inputProps}
			/>
		);
	}
}

const ObservedSyncCombobox = observer(SyncComboboxClass);

type loadingState = 'loading' | 'error' | 'done';

class AsyncComboboxClass<T, I> extends React.Component<InternalAsyncComboboxProps<T, I>> {
	@observable
	private requestState: loadingState = 'done';

	@observable
	private initialRequestState: loadingState = 'loading';

	@observable
	private optionResults: ComboboxOption<I | undefined>[] = [];

	@observable
	private hasSearched = false;

	private lastSearchQuery = '';

	public reFetch = () => this.searchOptions(this.lastSearchQuery);

	@action
	private searchOptions = (data: string): Promise<void> => {
		this.lastSearchQuery = data;
		if (data.length === 0) {
			this.hasSearched = false;
			this.optionResults.length = 0;
			this.requestState = 'done';
			return Promise.resolve();
		}
		this.hasSearched = true;
		this.requestState = 'loading';
		return this.props.options(data).then(this.onSearchSuccess).catch(this.onSearchFail);
	};

	@action
	private onSearchSuccess = (options: ComboboxOption<I | undefined>[], initial?: boolean) => {
		this.optionResults.length = 0;
		this.optionResults.push(...options);
		if (initial) {
			this.initialRequestState = 'done';
		} else {
			this.requestState = 'done';
		}
	};

	@action
	private onSearchFail = (error: any, initial?: boolean) => {
		console.error(error);
		if (initial) {
			this.initialRequestState = 'error';
		} else {
			this.requestState = 'error';
		}
	};

	@action
	private onOpen = () => {
		this.hasSearched = false;
		if (this.lastSearchQuery !== '') {
			this.searchOptions('');
		}
	};

	constructor(props: InternalAsyncComboboxProps<T, I>) {
		super(props);

		makeObservable(this);

		const { initialOptions } = this.props;

		if (initialOptions) {
			initialOptions()
				.then(_.partial(this.onSearchSuccess, _, true))
				.catch(_.partial(this.onSearchFail, _, true));
		} else {
			this.onSearchSuccess([], true);
		}
	}

	public render() {
		const {
			inputProps,
			isClearable,
			onChange,
			modelProperty,
			onAfterChange,
			optionEqualFunc,
			model,
			getOptionValue,
			placeholder,
			minSearchLength,
			isDisabled,
			noResultsMessage,
		} = this.props;

		// This switch exhausts all types so it does not need a default
		// eslint-disable-next-line default-case
		switch (this.initialRequestState) {
			case 'loading':
				return <CustomSpinner loaderClassname="mini" />;
			case 'error':
				return <div>There was an error loading the combobox data</div>;
			case 'done':
				return (
					<ObservedInnerCombobox
						model={model}
						modelProperty={modelProperty}
						getOptionValue={getOptionValue}
						search={options => options}
						optionResults={this.optionResults}
						onSearchChange={(e, data) => this.searchOptions(data.searchQuery)}
						placeholder={placeholder}
						noResultsMessage={
							this.hasSearched && this.requestState !== 'loading'
								? 'No results found.'
								: (noResultsMessage ?? 'Type to search...')
						}
						loading={this.requestState === 'loading'}
						disabled={isDisabled}
						minCharacters={minSearchLength}
						clearable={isClearable}
						onAfterChange={onAfterChange}
						onChange={onChange}
						optionEqualFunc={optionEqualFunc}
						onOpen={this.onOpen}
						{...inputProps}
					/>
				);
		}
	}
}

const ObservedAsyncCombobox = observer(AsyncComboboxClass);

interface InnerComboboxProps<T, I> extends StrictDropdownProps {
	model: T;
	modelProperty: BoundProp<T, I | I[]>;
	optionResults: ComboboxOption<I | undefined>[];
	getOptionValue: (option: I | undefined) => undefined | boolean | number | string;
	onAfterChange?: (event: React.SyntheticEvent<HTMLElement>, data: DropdownProps, oldValue?: I) => void;
	optionEqualFunc?: (modelProperty: string | number | boolean | undefined, option: I | undefined) => boolean;
}

class InnerComboboxClass<T, I> extends React.Component<InnerComboboxProps<T, I>> {
	constructor(props: InnerComboboxProps<T, I>) {
		super(props);
		makeObservable(this);
	}

	@computed
	private get options() {
		const { getOptionValue, optionResults } = this.props;

		return optionResults.map(option => ({
			text: option.display,
			content: option.content,
			value: getOptionValue(option.value),
			isFixed: option.isFixed,
			key: getOptionValue(option.value),
			disabled: option.disabled,
			className: option.className,
			description: option.description,
			optionprops: option.optionProps,
			as: (props: any) => (
				<div {..._.omit(props, 'isFixed')} data-id={getOptionValue(option.value)}>
					{props.children}
				</div>
			),
		}));
	}

	@computed
	private get customOptions() {
		const { getOptionValue, optionResults } = this.props;

		return optionResults.map(option => ({
			text: option.display,
			content: option.content,
			value: getOptionValue(option.value),
			isFixed: option.isFixed,
			key: getOptionValue(option.value),
			disabled: option.disabled,
			className: option.className,
			description: option.description,
			optionprops: option.optionProps,
			as: (props: any) => (
				<span {..._.omit(props, 'isFixed')} data-id={getOptionValue(option.value)}>
					{props.children}
				</span>
			),
		}));
	}

	@computed
	private get selectedItem() {
		const { modelProperty: modelPropertyProp, model, optionResults, multiple, getOptionValue } = this.props;

		const modelProperty = getBoundValue(model, modelPropertyProp);

		if (!(modelProperty === null || modelProperty === undefined)) {
			if (multiple && Array.isArray(modelProperty)) {
				return _.chain(optionResults)
					.filter(option => {
						return _.some(modelProperty, modelProp => this.optionsEqual(getOptionValue(modelProp), option));
					})
					.flatMap(option => {
						const value = getOptionValue(option.value);
						if (value !== undefined) {
							return value;
						}
						return [];
					})
					.value();
			}

			// Not the multi-select path, so the property holds a single option value.
			const singleValue = modelProperty as I | undefined;

			// If there is a value already selected in the model then we want that one to be selected
			const value = optionResults.find(option => {
				return this.optionsEqual(getOptionValue(singleValue), option);
			});
			if (value) {
				return getOptionValue(value.value);
			}
		}

		return '';
	}

	private set selectedItem(value: undefined | boolean | number | string | (boolean | number | string)[]) {
		const { modelProperty, model, optionResults } = this.props;

		if (Array.isArray(value)) {
			if (!Array.isArray(getBoundValue(model, modelProperty))) {
				setBoundValue(model, modelProperty, []);
			}

			// Read the array back off the model rather than mutating a local: MobX replaces an
			// assigned array with an observable one, so the stored array is the only one whose
			// mutations are tracked.
			const target = getBoundValue(model, modelProperty) as (I | undefined)[];

			target.length = 0;
			const selected = optionResults
				.filter(option => {
					return _.some(value, modelProp => this.optionsEqual(modelProp, option));
				})
				.map(option => option.value);
			target.push(...selected);
		} else {
			const selected = optionResults.find(option => {
				return this.optionsEqual(value, option);
			});

			if (!(selected === null || selected === undefined)) {
				setBoundValue(model, modelProperty, selected.value);
			} else {
				setBoundValue(model, modelProperty, undefined);
			}
		}
	}

	@action
	private onChange = (event: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
		// If the onChange is overwritten then we should just use that one instead
		if (this.props.onChange) {
			return this.props.onChange(event, data);
		}

		const oldSelectedItem = JSON.stringify(this.selectedItem);
		const { value } = data;
		this.selectedItem = value;

		// If there is any logic to be done after the change of the combobox, do it here
		if (this.props.onAfterChange) {
			this.props.onAfterChange(event, data, JSON.parse(oldSelectedItem));
		}
	};

	private optionsEqual(modelProp: string | number | boolean | undefined, option: ComboboxOption<I | undefined>) {
		const { getOptionValue, optionEqualFunc } = this.props;

		if (optionEqualFunc) {
			return optionEqualFunc(modelProp, option.value);
		}

		return getOptionValue(option.value) === modelProp;
	}

	public render() {
		const { multiple, disabled } = this.props;

		return (
			<Dropdown
				selection
				disabled={disabled}
				autoComplete="off"
				onChange={this.onChange}
				value={this.selectedItem}
				data-id={Array.isArray(this.selectedItem) ? this.selectedItem.join(',') : this.selectedItem}
				options={this.customOptions}
				className={classNames('dropdown__container', multiple ? undefined : 'single')}
				renderLabel={(item, index, defaultLabelProps) => {
					const key = typeof item.value === 'string' || typeof item.value === 'number' ? item.value : index;

					return (
						<>
							<Label
								{...defaultLabelProps}
								data-id={item.value}
								content={item.text}
								key={key}
								onRemove={item.isFixed ? undefined : defaultLabelProps.onRemove}
							/>
						</>
					);
				}}
				{..._.omit(
					this.props,
					'model',
					'modelProperty',
					'optionResults',
					'getOptionValue',
					'onAfterChange',
					'optionEqualFunc',
					'selection',
					'onChange',
					'value',
					'data-id',
					'options',
					'className',
					'renderLabel',
				)}
			/>
		);
	}
}

const ObservedInnerCombobox = observer(InnerComboboxClass);
